import { useEffect, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, FileText, KeyRound, MapPin, Pencil, Plus, Trash2, Undo2, Upload, Wrench } from 'lucide-react';
import { AssignmentCreateModal, AssignmentReturnModal } from '../components/forms/AssignmentModals';
import { CarFormModal } from '../components/forms/CarFormModal';
import { DocumentEditModal, DocumentUploadModal } from '../components/forms/DocumentModals';
import { ServiceFormModal } from '../components/forms/ServiceFormModal';
import { DeleteDocumentDialog, documentColumns, gpsMonthColumns, serviceColumns } from '../components/columns';
import { RecordHistory } from '../components/RecordHistory';
import { Badge, CarStatusBadge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Card, CardHeader, DetailItem, EmptyState } from '../components/ui/Card';
import { DataTable, type Column } from '../components/ui/DataTable';
import { ConfirmDialog } from '../components/ui/Modal';
import { PageLoader } from '../components/ui/Spinner';
import { Tabs } from '../components/ui/Tabs';
import {
  useAssignments,
  useCar,
  useCarSummary,
  useDeleteDocument,
  useDeleteService,
  useDocuments,
  useGPSMonths,
  useRetireCar,
  useServices,
  useUpdateCar,
} from '../hooks/useApi';
import { errorMessage } from '../services/api';
import { toast, useIsAdmin } from '../store';
import type { Assignment, Car, Document, Service } from '../types';
import {
  driverName,
  formatCurrency,
  formatDate,
  formatDateTime,
  formatKm,
  formatNumber,
  FUEL_LABELS,
  TIRE_SEASONS,
} from '../utils/helpers';
import { t, tx } from '../i18n';

export function CarDetail() {
  const carId = Number(useParams().id);
  const isAdmin = useIsAdmin();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get('tab') || 'overview';
  const setTab = (value: string) => setSearchParams(value === 'overview' ? {} : { tab: value }, { replace: true });

  const { data: car, isLoading, error } = useCar(carId);
  const { data: summary } = useCarSummary(carId);
  const [editing, setEditing] = useState(false);
  const [retiring, setRetiring] = useState(false);
  const retire = useRetireCar();

  if (isLoading) return <PageLoader />;
  if (error || !car) {
    return (
      <EmptyState title={t('Vozidlo sa nenašlo')}>
        <Link to="/cars" className="text-primary-600 hover:underline">
          {t('Späť na zoznam vozidiel')}
        </Link>
      </EmptyState>
    );
  }

  const handleRetire = async () => {
    try {
      await retire.mutateAsync(car.id);
      toast.success(t('Vozidlo bolo vyradené z prevádzky'));
      setRetiring(false);
    } catch (err) {
      toast.error(errorMessage(err));
      setRetiring(false);
    }
  };

  return (
    <>
      <Link to="/cars" className="mb-3 inline-flex items-center gap-1 text-sm text-secondary-500 hover:text-secondary-800">
        <ArrowLeft className="h-4 w-4" />  {t('Vozidlá')}
      </Link>
      <div className="mb-5 flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="font-mono text-2xl font-bold text-secondary-900">{car.license_plate}</h1>
            <CarStatusBadge status={car.status} />
          </div>
          <p className="mt-1 text-secondary-600">
            {car.brand} {car.model}
            {car.year && ` · ${car.year}`}
            {car.fuel_type && ` · ${FUEL_LABELS[car.fuel_type]}`} · {formatNumber(car.current_km)} km
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => setEditing(true)}>
            <Pencil className="h-4 w-4" />  {t('Upraviť')}
          </Button>
          {car.status !== 'retired' && (
            <Button variant="outline" className="text-red-600" onClick={() => setRetiring(true)}>
              <Trash2 className="h-4 w-4" />  {t('Vyradiť')}
            </Button>
          )}
        </div>
      </div>

      <Tabs
        className="mb-5"
        value={tab}
        onChange={setTab}
        tabs={[
          { value: 'overview', label: t('Prehľad') },
          { value: 'assignments', label: t('Pridelenie'), count: summary?.assignments },
          { value: 'services', label: t('Servisy'), count: summary?.services },
          { value: 'documents', label: t('Dokumenty'), count: summary?.documents },
          { value: 'rides', label: 'GPS jazdy', count: summary?.gps_months },
          { value: 'history', label: t('História zmien'), hidden: !isAdmin },
        ]}
      />

      {tab === 'overview' && <OverviewTab car={car} />}
      {tab === 'assignments' && <AssignmentsTab car={car} />}
      {tab === 'services' && <ServicesTab car={car} />}
      {tab === 'documents' && <DocumentsTab car={car} />}
      {tab === 'rides' && <RidesTab car={car} />}
      {tab === 'history' && isAdmin && (
        <Card>
          <RecordHistory table="cars" recordId={car.id} />
        </Card>
      )}

      <CarFormModal isOpen={editing} car={car} onClose={() => setEditing(false)} />
      <ConfirmDialog
        isOpen={retiring}
        onClose={() => setRetiring(false)}
        onConfirm={handleRetire}
        loading={retire.isPending}
        title={t('Vyradiť vozidlo')}
        confirmText={t('Vyradiť')}
        message={
          <>
            {tx('Vozidlo {plate} bude označené ako vyradené. História (servisy, dokumenty, jazdy) zostane zachovaná a stav je možné neskôr zmeniť úpravou vozidla.', {
              plate: <strong>{car.license_plate}</strong>,
            })}
          </>
        }
      />
    </>
  );
}

function OverviewTab({ car }: { car: Car }) {
  const { data: summary } = useCarSummary(car.id);
  const { data: active } = useAssignments({ car_id: car.id, active_only: true, page_size: 1 });
  const { data: services } = useServices({ car_id: car.id, page_size: 1 });
  const current = active?.assignments[0];
  const lastService = services?.services[0];
  const [assigning, setAssigning] = useState(false);
  const [returning, setReturning] = useState<Assignment | null>(null);

  return (
    <div className="grid gap-6 lg:grid-cols-3">
      <div className="space-y-6 lg:col-span-2">
        <Card>
          <CardHeader
            title={t('Aktuálne pridelenie')}
            action={
              current ? (
                <Button size="sm" variant="outline" onClick={() => setReturning({ ...current, car: { ...car } })}>
                  <Undo2 className="h-4 w-4" />  {t('Vrátiť vozidlo')}
                </Button>
              ) : (
                car.status === 'active' && (
                  <Button size="sm" onClick={() => setAssigning(true)}>
                    <KeyRound className="h-4 w-4" />  {t('Prideliť vodičovi')}
                  </Button>
                )
              )
            }
          />
          {current ? (
            <dl className="grid grid-cols-2 gap-4 sm:grid-cols-4">
              <DetailItem label={t('Vodič')}>{driverName(current.driver)}</DetailItem>
              <DetailItem label={t('Od')}>{formatDateTime(current.assigned_at)}</DetailItem>
              <DetailItem label={t('Počiatočné km')}>{formatNumber(current.start_km)}</DetailItem>
              <DetailItem label={t('Účel')}>{current.purpose || '–'}</DetailItem>
            </dl>
          ) : (
            <p className="text-sm text-secondary-500">
              {car.status === 'active' ? t('Vozidlo je voľné.') : t('Vozidlo nie je v aktívnej prevádzke, nie je možné ho prideliť.')}
            </p>
          )}
        </Card>

        <Card>
          <CardHeader title={t('Údaje o vozidle')} />
          <dl className="grid grid-cols-2 gap-4 sm:grid-cols-3">
            <DetailItem label={t('Značka a model')}>
              {car.brand} {car.model}
            </DetailItem>
            <DetailItem label={t('Rok výroby')}>{car.year ?? '–'}</DetailItem>
            <DetailItem label={t('Palivo')}>{car.fuel_type ? FUEL_LABELS[car.fuel_type] : '–'}</DetailItem>
            <DetailItem label="VIN">
              <span className="font-mono">{car.vin || '–'}</span>
            </DetailItem>
            <DetailItem label={t('Stav tachometra')}>{formatNumber(car.current_km)} km</DetailItem>
            <DetailItem label={t('Spotreba podľa TP')}>
              {car.official_consumption ? `${formatNumber(car.official_consumption, 1)} l/100 km` : t('nezadané')}
              {car.tank_capacity ? <span className="block text-xs text-secondary-500">{t('nádrž {liters} l', { liters: formatNumber(car.tank_capacity) })}</span> : null}
            </DetailItem>
            {car.gps_location && (
              <div className="col-span-2">
                <DetailItem label={t('Posledná poloha (GPS)')}>
                  <span className="flex items-start gap-1.5">
                    <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-primary-600" />
                    <span>
                      {car.gps_latitude != null && car.gps_longitude != null ? (
                        <a
                          href={`https://www.google.com/maps?q=${car.gps_latitude},${car.gps_longitude}`}
                          target="_blank"
                          rel="noreferrer"
                          className="text-primary-700 hover:underline"
                        >
                          {car.gps_location.replace(/^SK - /, '')}
                        </a>
                      ) : (
                        car.gps_location.replace(/^SK - /, '')
                      )}
                      <span className="block text-xs text-secondary-500">{formatDateTime(car.gps_location_at)}</span>
                    </span>
                  </span>
                </DetailItem>
              </div>
            )}
            <DetailItem label={t('Pneumatiky')}>
              <Link to="/tires" className="hover:underline">
                {car.tire_season ? `${TIRE_SEASONS[car.tire_season].label}${car.tire_brand ? ` · ${car.tire_brand}` : ''}${car.tire_size ? ` · ${car.tire_size}` : ''}` : t('nezadané')}
              </Link>
            </DetailItem>
            <DetailItem label={t('Posledná zmena')}>{formatDateTime(car.updated_at)}</DetailItem>
          </dl>
        </Card>

        <AutosaveNotes car={car} />
      </div>

      <div className="space-y-6">
        <Card>
          <CardHeader title={t('Súhrn')} />
          <dl className="space-y-3">
            <DetailItem label={t('Náklady na servis spolu')}>{formatCurrency(summary?.service_cost_total ?? 0)}</DetailItem>
            <DetailItem label={t('Najazdené podľa GPS')}>{formatKm(summary?.rides_distance_km ?? 0)}</DetailItem>
            <DetailItem label={t('Posledný servis')}>
              {lastService ? `${formatDate(lastService.service_date)} (${lastService.workshop || t('bez uvedenia servisu')})` : '–'}
            </DetailItem>
            <DetailItem label={t('Ďalší servis')}>
              {lastService?.next_service_date || lastService?.next_service_km ? (
                <>
                  {lastService.next_service_date && formatDate(lastService.next_service_date)}
                  {lastService.next_service_date && lastService.next_service_km !== null && ' / '}
                  {lastService.next_service_km !== null && `${formatNumber(lastService.next_service_km)} km`}
                </>
              ) : (
                '–'
              )}
            </DetailItem>
          </dl>
        </Card>
      </div>

      <AssignmentCreateModal isOpen={assigning} onClose={() => setAssigning(false)} carId={car.id} />
      <AssignmentReturnModal assignment={returning} onClose={() => setReturning(null)} />
    </div>
  );
}

/** Notes field that saves automatically shortly after the user stops typing. */
function AutosaveNotes({ car }: { car: Car }) {
  const update = useUpdateCar();
  const [value, setValue] = useState(car.notes ?? '');
  const [status, setStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const lastSaved = useRef(car.notes ?? '');

  // Pick up changes made elsewhere while the field is not being edited
  useEffect(() => {
    if (status === 'idle' || status === 'saved') {
      setValue(car.notes ?? '');
      lastSaved.current = car.notes ?? '';
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [car.notes]);

  useEffect(() => {
    if (value === lastSaved.current) return;
    setStatus('saving');
    const timer = window.setTimeout(async () => {
      try {
        await update.mutateAsync({ id: car.id, data: { notes: value.trim() || null } });
        lastSaved.current = value;
        setStatus('saved');
      } catch (err) {
        setStatus('error');
        toast.error(errorMessage(err, t('Poznámku sa nepodarilo uložiť')));
      }
    }, 800);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, car.id]);

  const label = { idle: '', saving: t('Ukladá sa…'), saved: t('Uložené'), error: t('Chyba pri ukladaní') }[status];

  return (
    <Card>
      <CardHeader title={t('Poznámky')} action={<span className={status === 'error' ? 'text-xs text-red-600' : 'text-xs text-secondary-500'}>{label}</span>} />
      <textarea
        value={value}
        onChange={(e) => setValue(e.target.value)}
        rows={4}
        placeholder={t('Poznámky k vozidlu sa ukladajú automaticky…')}
        aria-label={t('Poznámky k vozidlu')}
        className="w-full resize-y rounded-lg border border-secondary-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
      />
    </Card>
  );
}

function AssignmentsTab({ car }: { car: Car }) {
  const [page, setPage] = useState(1);
  const { data, isLoading, isFetching } = useAssignments({ car_id: car.id, page, page_size: 20 });
  const [assigning, setAssigning] = useState(false);
  const [returning, setReturning] = useState<Assignment | null>(null);
  const hasActive = data?.assignments.some((a) => !a.returned_at);

  const columns: Column<Assignment>[] = [
    { key: 'driver', header: t('Vodič'), render: (a) => <span className="font-medium">{driverName(a.driver)}</span> },
    { key: 'from', header: t('Od'), render: (a) => formatDateTime(a.assigned_at) },
    { key: 'to', header: t('Do'), render: (a) => (a.returned_at ? formatDateTime(a.returned_at) : <Badge variant="success" dot>{t('Požičané')}</Badge>) },
    { key: 'km', header: t('Km'), className: 'tabular-nums whitespace-nowrap', render: (a) => `${formatNumber(a.start_km)} → ${formatNumber(a.end_km)}` },
    {
      key: 'driven',
      header: t('Najazdené'),
      className: 'text-right tabular-nums',
      headerClassName: 'text-right',
      render: (a) => (a.end_km !== null && a.start_km !== null ? `${formatNumber(a.end_km - a.start_km)} km` : '–'),
    },
    { key: 'purpose', header: t('Účel'), render: (a) => a.purpose || '–' },
    {
      key: 'actions',
      header: <span className="sr-only">{t('Akcie')}</span>,
      className: 'text-right',
      render: (a) =>
        !a.returned_at && (
          <Button size="sm" variant="outline" onClick={() => setReturning({ ...a, car: { ...car } })}>
            {t('Vrátiť')}
          </Button>
        ),
    },
  ];

  return (
    <Card padding="none">
      <div className="flex items-center justify-between gap-3 p-4">
        <h2 className="font-semibold">{t('História pridelení')}</h2>
        {car.status === 'active' && !hasActive && (
          <Button size="sm" onClick={() => setAssigning(true)}>
            <Plus className="h-4 w-4" />  {t('Prideliť')}
          </Button>
        )}
      </div>
      <DataTable
        data={data?.assignments}
        columns={columns}
        rowKey={(a) => a.id}
        isLoading={isLoading}
        isFetching={isFetching}
        emptyMessage={t('Vozidlo zatiaľ nebolo nikomu pridelené')}
        pagination={data && { page, pageSize: 20, total: data.total, onPageChange: setPage }}
      />
      <AssignmentCreateModal isOpen={assigning} onClose={() => setAssigning(false)} carId={car.id} />
      <AssignmentReturnModal assignment={returning} onClose={() => setReturning(null)} />
    </Card>
  );
}

function ServicesTab({ car }: { car: Car }) {
  const [page, setPage] = useState(1);
  const { data, isLoading, isFetching } = useServices({ car_id: car.id, page, page_size: 20 });
  const [editing, setEditing] = useState<Service | null>(null);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<Service | null>(null);
  const remove = useDeleteService();

  const columns = serviceColumns({ onEdit: setEditing, onDelete: setDeleting, showCar: false });

  return (
    <Card padding="none">
      <div className="flex flex-wrap items-center justify-between gap-3 p-4">
        <div>
          <h2 className="font-semibold">{t('Servisná história')}</h2>
          {data && <p className="text-sm text-secondary-500">{t('Náklady spolu: {cost}', { cost: formatCurrency(data.total_cost) })}</p>}
        </div>
        <Button size="sm" onClick={() => setCreating(true)}>
          <Plus className="h-4 w-4" />  {t('Nový záznam')}
        </Button>
      </div>
      <DataTable
        data={data?.services}
        columns={columns}
        rowKey={(s) => s.id}
        isLoading={isLoading}
        isFetching={isFetching}
        emptyMessage={<EmptyState icon={<Wrench className="h-10 w-10" />} title={t('Žiadne servisné záznamy')} />}
        pagination={data && { page, pageSize: 20, total: data.total, onPageChange: setPage }}
      />
      <ServiceFormModal isOpen={creating} onClose={() => setCreating(false)} carId={car.id} />
      <ServiceFormModal isOpen={!!editing} service={editing} onClose={() => setEditing(null)} />
      <ConfirmDialog
        isOpen={!!deleting}
        onClose={() => setDeleting(null)}
        loading={remove.isPending}
        title={t('Zmazať servisný záznam')}
        confirmText={t('Zmazať')}
        message={t('Naozaj zmazať záznam zo dňa {serviceDate}?', { serviceDate: formatDate(deleting?.service_date) })}
        onConfirm={async () => {
          try {
            await remove.mutateAsync(deleting!.id);
            toast.success(t('Záznam bol zmazaný'));
          } catch (err) {
            toast.error(errorMessage(err));
          }
          setDeleting(null);
        }}
      />
    </Card>
  );
}

function DocumentsTab({ car }: { car: Car }) {
  const [page, setPage] = useState(1);
  const { data, isLoading, isFetching } = useDocuments({ car_id: car.id, page, page_size: 20 });
  const [uploading, setUploading] = useState(false);
  const [editing, setEditing] = useState<Document | null>(null);
  const [deleting, setDeleting] = useState<Document | null>(null);
  const remove = useDeleteDocument();

  return (
    <Card padding="none">
      <div className="flex items-center justify-between gap-3 p-4">
        <h2 className="font-semibold">{t('Dokumenty')}</h2>
        <Button size="sm" onClick={() => setUploading(true)}>
          <Upload className="h-4 w-4" />  {t('Nahrať')}
        </Button>
      </div>
      <DataTable
        data={data?.documents}
        columns={documentColumns({ onEdit: setEditing, onDelete: setDeleting, showCar: false })}
        rowKey={(d) => d.id}
        isLoading={isLoading}
        isFetching={isFetching}
        emptyMessage={<EmptyState icon={<FileText className="h-10 w-10" />} title={t('Žiadne dokumenty')} />}
        pagination={data && { page, pageSize: 20, total: data.total, onPageChange: setPage }}
      />
      <DocumentUploadModal isOpen={uploading} onClose={() => setUploading(false)} carId={car.id} />
      <DocumentEditModal document={editing} onClose={() => setEditing(null)} />
      <DeleteDocumentDialog document={deleting} onClose={() => setDeleting(null)} remove={remove} />
    </Card>
  );
}

function RidesTab({ car }: { car: Car }) {
  const [page, setPage] = useState(1);
  const { data, isLoading, isFetching } = useGPSMonths({ car_id: car.id, page, page_size: 24 });

  return (
    <Card padding="none">
      <div className="flex flex-wrap items-center justify-between gap-3 p-4">
        <div>
          <h2 className="font-semibold">{t('GPS – mesačné záznamy')}</h2>
          {data && (
            <p className="text-sm text-secondary-500">
              {formatNumber(data.total)} mesiacov · spolu {formatKm(data.total_distance_km)}
            </p>
          )}
        </div>
        <Link to="/gps?tab=import">
          <Button size="sm" variant="outline">
            <Upload className="h-4 w-4" />  {t('Import knihy jázd')}
          </Button>
        </Link>
      </div>
      <DataTable
        data={data?.months}
        columns={gpsMonthColumns({ showCar: false })}
        rowKey={(m) => m.id}
        isLoading={isLoading}
        isFetching={isFetching}
        emptyMessage={<EmptyState icon={<MapPin className="h-10 w-10" />} title={t('Žiadne importované mesiace')} />}
        pagination={data && { page, pageSize: 24, total: data.total, onPageChange: setPage }}
      />
    </Card>
  );
}
